import { useEffect, useRef, useState, type ReactNode } from "react";

export function WhatsAppQrShareControl({ children }: { children?: ReactNode }) {
  const [view, setView] = useState<"scan" | "share">("scan");
  const [link, setLink] = useState<{ url: string; deadline: number } | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const active = useRef(true);
  const scanRegion = useRef<HTMLDivElement>(null);
  // WHY [BOL-QR-SHARE-ASYNC-812]: donor 6bfdd19b fences OS share/clipboard
  // continuations by grant generation AND deadline. An old promise can settle
  // after expiry or replacement while this same component is still mounted.
  const generation = useRef(0);
  useEffect(() => { active.current = true; return () => { active.current = false; generation.current++; }; }, []);
  useEffect(() => {
    if (!link) return;
    const tick = () => {
      const seconds = Math.max(0, Math.ceil((link.deadline - Date.now()) / 1000));
      setRemaining(seconds);
      if (!seconds) { generation.current++; setLink(null); setMessage("Link expired. Create a new link to try again."); }
    };
    tick();
    const timer = window.setInterval(tick, 250);
    document.addEventListener("visibilitychange", tick);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", tick); };
  }, [link]);
  async function create() {
    if (busy) return;
    const requestGeneration = ++generation.current;
    setBusy(true); setLink(null); setMessage("");
    const startedAt = Date.now();
    try {
      const response = await fetch("/api/whatsapp/qr-share", {
        method: "POST", credentials: "same-origin", cache: "no-store",
        headers: { "Content-Type": "application/json" }, body: "{}"
      });
      if (!response.ok) throw new Error("Wait for a fresh QR code, then try again.");
      const value = await response.json() as { url: string; expiresAt: string };
      if (typeof value.url !== "string" || !value.url.startsWith(`${location.origin}/whatsapp-link#`)
        || !/^[A-Za-z0-9_-]{43}$/.test(value.url.split("#")[1] ?? "") || !Number.isFinite(Date.parse(value.expiresAt)))
        throw new Error("The link could not be created. Try again.");
      // WHY [BOL-QR-SHARE-LIFETIME-001]: never restart 60 seconds after network
      // delay, show a late response after connection/session rotation, or claim
      // an expired capability was copied. Server expiry remains authoritative.
      const deadline = Math.min(Date.parse(value.expiresAt), startedAt + 60_000);
      if (deadline <= Date.now()) throw new Error("Link expired. Create a new link to try again.");
      if (!active.current || requestGeneration !== generation.current) return;
      setRemaining(Math.ceil((deadline - Date.now()) / 1000));
      setLink({ url: value.url, deadline });
      setMessage("Open this link on another phone or computer, then scan it with your business phone.");
    } catch { if (active.current && requestGeneration === generation.current) setMessage("The link is unavailable. Wait for a fresh QR code, then try again."); }
    finally { if (active.current && requestGeneration === generation.current) setBusy(false); }
  }
  async function copy() {
    if (!link || link.deadline <= Date.now()) return;
    const requestGeneration = generation.current;
    const current = () => active.current && requestGeneration === generation.current && link.deadline > Date.now();
    try { await navigator.clipboard.writeText(link.url); if (current()) setMessage("Link copied. Open it on the other screen."); }
    catch { if (current()) setMessage("Select and copy the link below."); }
  }
  async function share() {
    if (!link || link.deadline <= Date.now()) return;
    const requestGeneration = generation.current;
    try { await navigator.share({ title: "Connect WhatsApp", url: link.url }); }
    catch (error) {
      if (active.current && requestGeneration === generation.current && link.deadline > Date.now()
        && !(error instanceof DOMException && error.name === "AbortError")) setMessage("Sharing is unavailable here. Copy the link instead.");
    }
  }
  // WHY [BOL-QR-SCAN-JUMP-R896]: the top choice already selects Scan, but the
  // actual QR can sit below the phone instructions. Navigate to that existing
  // code without creating a new link, refreshing QR, or touching pairing state.
  function showScan() {
    setView("scan");
    window.requestAnimationFrame(() => {
      const target = scanRegion.current?.querySelector<HTMLElement>(".qr-frame") ?? scanRegion.current;
      target?.scrollIntoView({
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        block: "center"
      });
    });
  }
  // WHY [BOL-QR-TOP-CHOICES-863]: both choices must be immediately visible on
  // a phone. Switching presentation must not remount this pairing-keyed grant
  // owner, mint a replacement link, extend its deadline, or stop QR refresh.
  return <div className="connection-options">
    <div className="connection-options__choices" role="group" aria-label="Choose how to scan">
      <button type="button" aria-pressed={view === "scan"} onClick={showScan}>Scan here</button>
      <button className="connection-options__share" type="button" aria-pressed={view === "share"} onClick={() => setView("share")}>Share scan link</button>
    </div>
    <div className="connection-options__scan" ref={scanRegion} hidden={view !== "scan"}>{children}</div>
    <div className="connection-share" hidden={view !== "share"}>
    <p>WhatsApp on this phone? Open a 60-second link on another screen. No login needed there.</p>
    <div className="connection-share__actions">
    <button className="secondary-button" type="button" disabled={busy} onClick={() => void create()}>
      {busy ? "Creating link…" : link ? "New link" : "Create 60-second link"}
    </button>
    {link ? <>
      <button className="primary-button" type="button" onClick={() => void copy()}>Copy link</button>
      {typeof navigator.share === "function" ? <button className="secondary-button" type="button" onClick={() => void share()}>Share…</button> : null}
    </> : null}
    </div>
    {link ? <><p className="connection-share__timer">Expires in <strong>{remaining}s</strong> · New link replaces the old one.</p><label className="field">Temporary QR link<input aria-label="Temporary QR link" readOnly
      value={link.url} size={24} onFocus={event => event.currentTarget.select()} /></label>
    </> : null}
    <p role="status">{message}</p>
    </div>
  </div>;
}
